import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssessmentHistory, getStudent } from '../../services/studentService';
import { getFileView } from '../../services/fileCabinetService';
import Loader from '../common/Loader';
import DocumentViewer from '../common/DocumentViewer';
import CitationContext from '../common/citations/CitationContext';
import { VerifiedAssessmentCard } from './SectionExtractedDataEditor';

const HEADLINE_KEYS = ['standard_score', 'index_score', 'composite_score', 'scaled_score', 't_score'];

function headlineText(h) {
  if (!h) return null;
  const v = h.values || {};
  const key = HEADLINE_KEYS.find((k) => v[k] !== undefined && v[k] !== null && String(v[k]).trim() !== '');
  if (!key) return null;
  const pct = v.percentile ? ` · ${v.percentile} %ile` : '';
  return `${v[key]}${pct}`;
}

function raterLabel(a) {
  const form = a.report_type ? a.report_type.replace(/_/g, ' ') : '';
  const who = a.rater_name || a.rater || '';
  return [who, form].filter(Boolean).join(' · ') || '—';
}

/** History administration in the row shape the evaluation section's score card reads. */
function cardRow(a) {
  return {
    test: a.test,
    date: a.date,
    date_source: a.date_source,
    examiner: a.examiner,
    raters: a.rater_name || (a.rater && a.rater !== 'examiner' ? a.rater : null),
    form: a.report_type ? a.report_type.replace(/_/g, ' ') : null,
    score_rows: a.scores || [],
    checks: (a.flags || []).join('; ') || null,
    _source: a.cover_page ? { page: a.cover_page, pages: a.pages || [], quote: null } : null,
  };
}

function AdministrationRow({ a, open, onToggle, onOpenSource, showTest }) {
  const ctx = useMemo(() => ({
    openSource: onOpenSource,
    defaultFileId: a.file_id,
    defaultFileName: a.file_name,
  }), [a.file_id, a.file_name, onOpenSource]);
  const cell = 'px-3 py-2 text-xs text-gray-700 dark:text-slate-200 align-top';
  return (
    <>
      <tr className="border-t border-gray-100 dark:border-slate-700">
        <td className={cell}>
          <button type="button" onClick={onToggle} aria-expanded={open}
            className="inline-flex items-center gap-1.5 font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-300">
            <span className="text-[10px] leading-none">{open ? '▾' : '▸'}</span>
            <span title={a.date_source ? `Date read from ${a.date_source}` : undefined}>{showTest ? a.test : (a.date || 'Undated')}</span>
          </button>
        </td>
        <td className={cell}>{raterLabel(a)}</td>
        <td className={`${cell} tabular-nums`}>
          {headlineText(a.headline) || <span className="text-gray-400">—</span>}
          {a.headline ? <span className="block text-[10px] text-gray-400 dark:text-slate-500">{a.headline.name}</span> : null}
        </td>
        <td className={`${cell} text-[11px] text-gray-500 dark:text-slate-400`}>
          {a.file_name}{a.cover_page ? ` · p.${a.cover_page}` : ''}
          {a.also_cited_on?.length ? (
            <span className="block">also p.{a.also_cited_on.map((x) => x.cover_page).filter(Boolean).join(', p.')}</span>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={4} className="px-3 pb-3">
            <CitationContext.Provider value={ctx}>
              {a.scores?.length
                ? <VerifiedAssessmentCard row={cardRow(a)} compact />
                : <p className="text-xs text-gray-500 dark:text-slate-400">No scores were read for this administration.</p>}
            </CitationContext.Provider>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AdministrationTable({ items, openKeys, toggle, onOpenSource, keyOf, showTest = false }) {
  const head = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400';
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="bg-gray-50 dark:bg-slate-900">
          <tr>
            <th className={head}>{showTest ? 'Test' : 'Date'}</th>
            <th className={head}>Rater / form</th>
            <th className={head}>Summary score</th>
            <th className={head}>Source</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => {
            const k = keyOf(a);
            return (
              <AdministrationRow key={k} a={a} open={!!openKeys[k]} onToggle={() => toggle(k)} onOpenSource={onOpenSource} showTest={showTest} />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AssessmentHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openKeys, setOpenKeys] = useState({});
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);
  const [viewerQuote, setViewerQuote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([getStudent(id), getAssessmentHistory(id)]).then(([s, h]) => {
      if (cancelled) return;
      if (s.status === 'fulfilled') setStudent(s.value.data.data);
      if (h.status === 'fulfilled') {
        setHistory(h.value.data.data);
        setError(null);
      } else {
        setError(h.reason?.response?.data?.detail || 'Could not load the assessment history.');
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id]);

  const onOpenSource = useCallback(async (req) => {
    if (!req?.fileId) return;
    try {
      const { data } = await getFileView(id, req.fileId, req.page);
      const fileData = data.data || data;
      setViewerFile({
        file_name: fileData.file_name || req.fileName || 'Document',
        mime_type: fileData.mime_type || 'application/pdf',
        file_type: 'evaluation',
        student_id: id,
        id: fileData.student_file_id || req.fileId,
        student_file_id: fileData.student_file_id || req.fileId,
      });
      setViewerPage(fileData.page || req.page || null);
      setViewerQuote(req.quote || null);
    } catch {
      setError('Could not load file for viewing.');
    }
  }, [id]);

  const keyOf = (a) => `${a.document_id}:${a.test_id}:${a.date || ''}:${a.rater_kind}:${a.cover_page}`;
  const toggle = (k) => setOpenKeys((prev) => ({ ...prev, [k]: !prev[k] }));

  const notShown = (a) => !a.used_in_cycles?.length;
  const tests = (history?.tests || [])
    .map((t) => ({ ...t, administrations: t.administrations.filter(notShown) }))
    .filter((t) => t.administrations.length);
  const undated = (history?.undated || []).filter(notShown);
  const count = tests.reduce((n, t) => n + t.administrations.length, 0) + undated.length;

  if (loading) return <Loader fullScreen text="Loading tests..." />;

  return (
    <div className="px-3 sm:px-6 pt-3 sm:pt-6 pb-8">
      <div className="mb-4 flex items-center gap-2 sm:gap-3 min-w-0">
        <button type="button" onClick={() => navigate(`/students/${id}/information`)}
          className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          aria-label="Back to student report">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div className="min-w-0">
          <h1 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white truncate">Tests not in any IEP cycle</h1>
          {student && <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 truncate">{student.student_name}</p>}
        </div>
      </div>

      {error ? (
        <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {history ? (
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Standardized tests read from this student&apos;s files that no IEP cycle shows in any section.
            Read-only: scores come from the assessment reader; check them against the source page.
          </p>

          {history.files_without_engine?.length ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-200">
              {history.files_without_engine.length} file{history.files_without_engine.length === 1 ? ' was' : 's were'} processed
              before the assessment reader was on, so {history.files_without_engine.length === 1 ? 'its' : 'their'} tests are not listed:{' '}
              {history.files_without_engine.map((f) => f.file_name).join(', ')}.
            </div>
          ) : null}

          {count === 0 ? (
            <p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              Every test read from the files is shown in at least one IEP cycle.
            </p>
          ) : (
            <p className="text-xs text-gray-600 dark:text-slate-300">
              <b className="tabular-nums">{count}</b> administration{count === 1 ? '' : 's'} not shown in any cycle
            </p>
          )}

          {tests.map((t) => (
            <section key={t.test_id} className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <header className="flex items-baseline gap-3 border-b border-gray-100 px-3 py-2 dark:border-slate-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t.test}</h2>
                {t.family ? <span className="text-[11px] text-gray-500 dark:text-slate-400">{t.family}</span> : null}
                <span className="ml-auto text-[11px] text-gray-500 dark:text-slate-400">
                  {t.administrations.length} administration{t.administrations.length === 1 ? '' : 's'}
                </span>
              </header>
              <AdministrationTable items={t.administrations} openKeys={openKeys} toggle={toggle} onOpenSource={onOpenSource} keyOf={keyOf} />
            </section>
          ))}

          {undated.length ? (
            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <header className="border-b border-gray-100 px-3 py-2 dark:border-slate-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Undated</h2>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">No test date was read and no cycle names these tests; check the source page.</p>
              </header>
              <AdministrationTable
                items={undated}
                openKeys={openKeys} toggle={toggle} onOpenSource={onOpenSource}
                keyOf={keyOf} showTest
              />
            </section>
          ) : null}
        </div>
      ) : null}

      <DocumentViewer
        open={!!viewerFile}
        file={viewerFile}
        page={viewerPage}
        highlightQuote={viewerQuote}
        studentId={id}
        onClose={() => { setViewerFile(null); setViewerPage(null); setViewerQuote(null); }}
      />
    </div>
  );
}
